/* istanbul ignore file */

import type { NextPage } from 'next';
import { SWRConfig } from 'swr';
import type { Units } from '@surfline/web-common';

import { unstable_serialize as serializeInfiniteKey } from 'swr/infinite';

import { getUser } from 'common/api/user';
import { type SpotRecommendationsResponse } from 'common/api/spotRecommendations';
import { createParamString, doFetch } from 'common/baseFetch';
import type { PromoboxResponse } from 'components/EditorialCarousel/EditorialCarousel';
import Homepage from 'containers/Homepage';
import { UserTypeContextProvider } from 'contexts/UserTypeContext';
import {
  getSpotRecommendationsKey,
  SPOT_RECOMMENDATIONS_PAGE_SIZE,
} from 'hooks/useSpotRecommendations';
import { getOrCreateStore } from 'stores';
import { SL_WEB_SPOT_RECOMMENDATIONS } from 'common/treatments';
import type { SwellEvents } from 'types/swellEvents';
import type { WavetrakContext } from 'types/wavetrak';
import { getServerTreatments } from 'utils/serverSplitClient';
import { getUserGeotarget, getUserCountryCode } from 'utils/user';
import { userToFavouritesResponseTransformer } from 'utils/transformers';
import swellEventsFetch from 'utils/swellEventsFetch';
import { getPopularSpots } from 'utils/favoritesFetch';
import addCustomUserAgentHeader from 'utils/addCustomUserAgentHeader';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';

export interface WavetrakServerSidePropsContext extends WavetrakContext {
  resolvedUrl: string;
}

interface BasePageProps {
  colorSchemeIndex: number;
  entitlements: string[];
  fallback: Map<string, object>;
  geotarget: string;
  isAnonUser: boolean;
  units: Units;
  userCountryCode: string;
}

const Base: NextPage<BasePageProps> = ({
  colorSchemeIndex,
  entitlements,
  fallback,
  geotarget,
  isAnonUser,
  units,
  userCountryCode,
}) => (
  <SWRConfig value={{ fallback }}>
    <UserTypeContextProvider isAnonUser={isAnonUser} entitlements={entitlements}>
      <Homepage
        colorSchemeIndex={colorSchemeIndex}
        geotarget={geotarget}
        units={units}
        userCountryCode={userCountryCode}
      />
    </UserTypeContextProvider>
  </SWRConfig>
);

export const getServerSideProps = async (context: WavetrakServerSidePropsContext) => {
  const isServer = true;
  const user = await getUser(
    {
      ...context,
      pathname: context.resolvedUrl,
    },
    isServer,
  );

  const requestOptions = {
    headers: { ...addCustomUserAgentHeader(true) },
  };
  const geotarget = getUserGeotarget(user);
  const userCountryCode = getUserCountryCode(user);
  const favoritesFallback = userToFavouritesResponseTransformer(user);

  let editorialsFallback: PromoboxResponse = {
    data: {
      promobox: [],
    },
  };

  let swellEventsFallback: SwellEvents = [];
  const editorialFetchUrl = `/feed/promobox?${createParamString({ geotarget, limit: 9 })}`;
  const isAnonUser = !user?.details;
  const swellEventsFetchUrl = `/feed/events?${createParamString({ geotarget })}`;

  try {
    const editorialResponse = await doFetch(editorialFetchUrl, requestOptions);
    const promobox = editorialResponse?.data?.promobox ?? [];
    editorialsFallback = {
      data: {
        promobox: promobox.slice(0, 9),
      },
    };
  } catch (err) {
    reportErrorToNewRelic(err as Error, 'index', {});
  }

  try {
    swellEventsFallback = await swellEventsFetch(swellEventsFetchUrl, requestOptions);
  } catch (err) {
    reportErrorToNewRelic(err as Error, 'index', {});
  }

  // skip fetching from /spots/popular (aka favorites fallback) endpoint if user is not anonymous
  const spotsFallback = !user?.details
    ? { '/kbyg/spots/popular?': await getPopularSpots(userCountryCode) }
    : {};

  // SSR-prefetch the first page of spot recommendations into SWR's cache.
  // serializeInfiniteKey (swr/infinite's unstable_serialize) produces the cache key that
  // useSWRInfinite expects, and getSpotRecommendationsKey is the shared key factory from
  // useSpotRecommendations — using it here ensures the SSR data hydrates into the client cache.
  // Only prefetch when the spot recommendations treatment is on — the client hook is gated
  // on the same treatment, so prefetched data would otherwise be fetched and discarded.
  const treatments = getServerTreatments(
    [SL_WEB_SPOT_RECOMMENDATIONS],
    user,
    context.req?.cookies?.ajs_anonymous_id,
  );
  const isSpotRecommendationsEnabled = treatments?.[SL_WEB_SPOT_RECOMMENDATIONS] === 'on';

  let recommendationsFallback: Record<string, Array<SpotRecommendationsResponse>> = {};
  if (
    isSpotRecommendationsEnabled &&
    user?.details &&
    user?.location?.latitude != null &&
    user?.location?.longitude != null
  ) {
    try {
      const lat = user.location.latitude;
      const lon = user.location.longitude;
      const firstSubregionId =
        user.favorites?.reduce<string | null>((found, fav: any) => {
          if (found) return found;
          return fav.subregionId ?? null;
        }, null) ?? null;
      const data = await doFetch<SpotRecommendationsResponse>(
        `/kbyg/spots/recommendations?${createParamString({
          lat,
          lon,
          subregionId: firstSubregionId,
          offset: 0,
          limit: SPOT_RECOMMENDATIONS_PAGE_SIZE,
        })}`,
        { ...requestOptions, cookies: context.req?.cookies },
      );
      const key = serializeInfiniteKey(() =>
        getSpotRecommendationsKey(lat, lon, firstSubregionId, 0),
      );
      recommendationsFallback = { [key]: [data] };
    } catch (err) {
      reportErrorToNewRelic(err as Error, 'index-recommendations', {});
    }
  }

  const store = getOrCreateStore();
  const state = store.getState();

  // Use a deterministic color scheme index to prevent hydration mismatches
  // Hash the user ID or use a default value to ensure consistency
  const deterministicColorSchemeIndex = user?.details?._id
    ? String(user.details._id).charCodeAt(0) % 6
    : 0;

  return {
    props: {
      colorSchemeIndex: deterministicColorSchemeIndex,
      enableCssBaseline: true,
      enableTheme: true,
      entitlements: user?.entitlements,
      fallback: {
        [editorialFetchUrl]: editorialsFallback,
        '/kbyg/favorites?': favoritesFallback,
        [swellEventsFetchUrl]: swellEventsFallback,
        ...spotsFallback,
        ...recommendationsFallback,
      },
      geotarget,
      isAnonUser,
      isAppBannerEnabled: true,
      isFavoritesBarVisible: !isAnonUser,
      ssrReduxState: state,
      units: user?.settings?.units,
      userCountryCode,
    },
  };
};

export default Base;
